import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Send, Eye, Mail, Clock, ArrowLeft, FlaskConical } from "lucide-react";
import { createCampaign, updateCampaign, getCampaign, testCampaign, sendCampaignNow, previewAudience } from "@/lib/admin-api";
import { toast } from "sonner";

const tipos = ["estacionamiento", "industria", "escuela", "residencial", "deportivo", "comercial", "otro"];
const ciudades = ["Monterrey / MTY", "Saltillo", "Torreón", "Chihuahua", "Tamaulipas", "Bajío", "CDMX", "Otra ciudad"];

const DEFAULT_BODY = `<h2 style="color:#0a0a0a">Hola {{first_name}},</h2>
<p style="font-size:15px;line-height:1.65;color:#374151">
  Tenemos novedades que te pueden interesar para tu proyecto FlexShade...
</p>
<p style="text-align:center;margin:28px 0">
  <a href="https://flexshade.mx/#cotizar" style="background:#DC2626;color:white;padding:14px 28px;text-decoration:none;border-radius:10px;font-weight:700">Solicita tu cotización gratis</a>
</p>
<p style="font-size:14px;color:#6b7280">— El equipo Eclipse
</p>`;

export default function AdminCampaignEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [audience, setAudience] = useState(null);

  const [form, setForm] = useState({
    name: "",
    subject_a: "",
    subject_b: "",
    ab_enabled: false,
    body_html: DEFAULT_BODY,
    scheduled_at: "",
    segment: {
      ciudad: "",
      tipo_espacio: "",
      source: "",
      include_leads: false,
      only_status: "",
    },
  });

  useEffect(() => {
    if (!editing) return;
    getCampaign(id).then(({ campaign }) => {
      setForm({
        name: campaign.name || "",
        subject_a: campaign.subject_a || "",
        subject_b: campaign.subject_b || "",
        ab_enabled: Boolean(campaign.ab_enabled),
        body_html: campaign.body_html || DEFAULT_BODY,
        scheduled_at: campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : "",
        segment: { ciudad: "", tipo_espacio: "", source: "", include_leads: false, only_status: "", ...(campaign.segment || {}) },
      });
    }).catch(() => toast.error("Error cargando campaña")).finally(() => setLoading(false));
  }, [id, editing]);

  const save = async () => {
    if (!form.name || !form.subject_a || !form.body_html) {
      toast.error("Completa nombre, asunto y contenido");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        subject_b: form.ab_enabled ? form.subject_b : null,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      };
      let saved;
      if (editing) {
        await updateCampaign(id, payload);
        saved = { id };
      } else {
        saved = await createCampaign(payload);
      }
      toast.success("Guardado");
      nav(`/admin/campaigns/${saved.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const doTest = async () => {
    if (!editing) {
      toast.error("Guarda primero para poder probar");
      return;
    }
    const email = prompt("Email para recibir la prueba:", testEmail || "");
    if (!email) return;
    setTestEmail(email);
    try {
      const r = await testCampaign(id, email);
      if (r.status === "sent") toast.success(`Prueba enviada a ${email}`);
      else toast.error("Fallo: " + (r.error || r.reason || "desconocido"));
    } catch {
      toast.error("Error enviando prueba");
    }
  };

  const doPreviewAud = async () => {
    if (!editing) { toast.error("Guarda primero"); return; }
    try {
      const r = await previewAudience(id);
      setAudience(r);
    } catch { toast.error("Error calculando audiencia"); }
  };

  const doSendNow = async () => {
    if (!editing) { toast.error("Guarda primero"); return; }
    if (!window.confirm("¿Enviar esta campaña AHORA a toda la audiencia seleccionada?")) return;
    try {
      await sendCampaignNow(id);
      toast.success("Envío iniciado en background");
      nav(`/admin/campaigns/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error iniciando envío");
    }
  };

  if (loading) return <div className="p-8 text-neutral-500">Cargando...</div>;

  return (
    <div className="p-5 md:p-8 max-w-5xl">
      <button onClick={() => nav("/admin/campaigns")} className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver
      </button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h1 className="section-title text-3xl md:text-4xl font-black">
          {editing ? "Editar campaña" : "Nueva campaña"}
        </h1>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border border-neutral-300 hover:bg-neutral-50" data-testid="preview-btn">
            <Eye className="w-4 h-4" /> Vista previa
          </button>
          <button onClick={doTest} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border border-neutral-300 hover:bg-neutral-50" data-testid="test-btn">
            <FlaskConical className="w-4 h-4" /> Enviar prueba
          </button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm disabled:opacity-60" data-testid="save-btn">
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
          </button>
          {editing && (
            <button onClick={doSendNow} className="bg-green-600 text-white hover:bg-green-700 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold" data-testid="send-now-btn">
              <Send className="w-4 h-4" /> Enviar ahora
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Section title="Identidad">
            <Field label="Nombre interno *">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input w-full" placeholder="Ej: Promo agosto 2026" data-testid="name-input" />
            </Field>
            <Field label="Asunto (A) *">
              <input value={form.subject_a} onChange={(e) => setForm({ ...form, subject_a: e.target.value })} className="form-input w-full" placeholder="{{first_name}}, nuevo modelo FlexShade disponible" data-testid="subject-a-input" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-neutral-700 mt-1">
              <input type="checkbox" checked={form.ab_enabled} onChange={(e) => setForm({ ...form, ab_enabled: e.target.checked })} data-testid="ab-enabled" />
              Activar A/B testing (50/50)
            </label>
            {form.ab_enabled && (
              <Field label="Asunto (B)">
                <input value={form.subject_b} onChange={(e) => setForm({ ...form, subject_b: e.target.value })} className="form-input w-full" placeholder="Variante B del asunto" data-testid="subject-b-input" />
              </Field>
            )}
          </Section>

          <Section title="Contenido HTML">
            <p className="text-xs text-neutral-500 mb-2">Usa <code>{`{{first_name}}`}</code>, <code>{`{{nombre}}`}</code>, <code>{`{{ciudad}}`}</code>, <code>{`{{email}}`}</code> como variables de personalización.</p>
            <textarea value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} rows={16} className="form-input w-full font-mono text-xs" data-testid="body-html-input" />
          </Section>
        </div>

        <div className="space-y-5">
          <Section title="Segmentación">
            <p className="text-xs text-neutral-500 mb-2">Envía solo a suscriptores que cumplan estos filtros. En blanco = todos los activos.</p>
            <Field label="Ciudad">
              <select value={form.segment.ciudad} onChange={(e) => setForm({ ...form, segment: { ...form.segment, ciudad: e.target.value } })} className="form-input w-full">
                <option value="">Todas</option>
                {ciudades.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Tipo de espacio">
              <select value={form.segment.tipo_espacio} onChange={(e) => setForm({ ...form, segment: { ...form.segment, tipo_espacio: e.target.value } })} className="form-input w-full">
                <option value="">Todos</option>
                {tipos.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Fuente de suscripción">
              <select value={form.segment.source} onChange={(e) => setForm({ ...form, segment: { ...form.segment, source: e.target.value } })} className="form-input w-full">
                <option value="">Cualquiera</option>
                <option value="popup">Popup</option>
                <option value="footer">Footer</option>
                <option value="csv">CSV</option>
                <option value="website">Sitio</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input type="checkbox" checked={form.segment.include_leads} onChange={(e) => setForm({ ...form, segment: { ...form.segment, include_leads: e.target.checked } })} data-testid="include-leads" />
              Incluir leads con email
            </label>
            {form.segment.include_leads && (
              <Field label="Solo leads con estatus">
                <select value={form.segment.only_status} onChange={(e) => setForm({ ...form, segment: { ...form.segment, only_status: e.target.value } })} className="form-input w-full">
                  <option value="">Todos</option>
                  <option value="nuevo">Nuevo</option>
                  <option value="contactado">Contactado</option>
                  <option value="seguimiento">Seguimiento</option>
                  <option value="cotizacion_enviada">Cotización enviada</option>
                  <option value="ganado">Ganado</option>
                  <option value="perdido">Perdido</option>
                </select>
              </Field>
            )}
            {editing && (
              <button onClick={doPreviewAud} className="mt-2 w-full text-sm px-3 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50" data-testid="preview-audience-btn">
                Calcular audiencia
              </button>
            )}
            {audience && (
              <div className="mt-2 text-sm bg-orange-50 border border-orange-200 rounded-lg p-3">
                <strong>{audience.total}</strong> destinatarios calculados.
                {audience.sample?.length > 0 && (
                  <ul className="mt-1 text-xs text-neutral-700 max-h-24 overflow-y-auto">
                    {audience.sample.map((s, i) => <li key={i}>{s.email}</li>)}
                  </ul>
                )}
              </div>
            )}
          </Section>

          <Section title="Programación">
            <Field label="Programar envío (opcional)">
              <div className="relative">
                <Clock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="form-input pl-9 w-full" data-testid="schedule-input" />
              </div>
              <p className="text-xs text-neutral-500 mt-1">Si defines una fecha futura, la campaña se envía automáticamente (chequeo cada 2 min).</p>
            </Field>
          </Section>
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="font-black section-title">Vista previa</h3>
              <button onClick={() => setShowPreview(false)} className="text-neutral-500 hover:text-neutral-900">✕</button>
            </div>
            <div className="px-5 py-3 border-b border-neutral-200 bg-neutral-50">
              <p className="text-xs text-neutral-500 uppercase">Asunto</p>
              <p className="font-bold">{form.subject_a || "(sin asunto)"}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe title="preview" srcDoc={form.body_html} className="w-full h-[480px] border-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <h3 className="text-sm font-black uppercase tracking-wider text-neutral-700 mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-neutral-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
